/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview Centralized user-facing strings for voice UI components
 * @version 1.0.0
 * @since v0.0.07
 *
 * Purpose: Prepare voice UI strings for future localization without introducing
 * an i18n library. All user-facing text should be imported from here.
 *
 * Note: Contract-bound strings (mode names, enums) are NOT included here.
 * Those remain in component constants to maintain contract alignment.
 */

/**
 * Voice Controls Messages
 */
export const voiceControls = {
  region: {
    ariaLabel: 'Voice controls'
  },
  controlsGroup: {
    ariaLabel: 'Voice session controls'
  },
  join: {
    label: 'Join Voice',
    joining: 'Joining...',
    ariaLabel: 'Join voice session',
    ariaLabelJoining: 'Joining voice session',
    title: 'Join Voice Session'
  },
  leave: {
    label: 'Leave',
    ariaLabel: 'Leave voice session',
    title: 'Leave Voice Session'
  },
  mute: {
    label: 'Mute Microphone',
    unmute: 'Unmute Microphone',
    ariaLabelMute: 'Mute microphone',
    ariaLabelUnmute: 'Unmute microphone',
    titleMute: 'Mute Microphone',
    titleUnmute: 'Unmute Microphone'
  },
  mode: {
    label: 'Mode:',
    ariaLabelGroup: 'Voice listening mode'
  },
  // Added 2026-02-23: volume slider strings for localization readiness
  volume: {
    label: 'Volume:',
    ariaLabel: 'Output volume: {percent}%',
    valueText: '{percent} percent'
  },
  buffer: {
    label: 'Buffer:',
    ariaLabel: 'Jitter buffer preset selection',
    title: 'Jitter Buffer Preset'
  },
  modes: {
    transcribe_all: {
      label: 'Transcribe All',
      description: 'The Myth Master™ processes everything you say. Best for active conversations. (Default)',
      icon: 'dYZT‹,?'
    },
    wake_word: {
      label: 'Hey MM™',
      description: 'The Myth Master™ only responds when you say "Hey MM". Saves processing when you\'re chatting with other players.',
      icon: 'dY`,'
    },
    off: {
      label: 'Voice Off',
      description: 'Voice transcription disabled. Use text chat or switch back to a voice mode to re-enable.',
      icon: 'dY"Ø'
    }
  },
  error: {
    dismiss: 'Dismiss error message',
    ariaLabel: 'Dismiss error message',
    connection: 'Connection error',
    micInit: 'Failed to initialize microphone',
    playbackInit: 'Failed to initialize audio playback',
    captureStart: 'Failed to start audio capture',
    joinFailed: 'Failed to join voice session'
  },
  status: {
    connected: 'Connected',
    connecting: 'Connecting...',
    reconnecting: 'Reconnecting...',
    error: 'Error',
    disconnected: 'Disconnected'
  },
  // Updated 2026-02-23: PTT/VA toggle strings for Section 5.5 Voice Bridge UI Wiring
  inputMode: {
    label: 'Input Mode:',
    ariaLabelGroup: 'Voice input mode selection',
    ptt: {
      label: 'Push to Talk',
      shortLabel: 'PTT',
      ariaLabel: 'Push to Talk mode: hold key to transmit',
      description: 'Hold a key to transmit your voice. Release to stop.',
      activeHint: 'Hold Space or click and hold to talk'
    },
    voiceActivation: {
      label: 'Voice Activation',
      shortLabel: 'VA',
      ariaLabel: 'Voice Activation mode: automatic voice detection',
      description: 'Your voice is detected and transmitted automatically.',
      activeHint: 'Speak normally — your mic is live'
    }
  }
} as const;

/**
 * Voice Status Messages
 */
export const voiceStatus = {
  region: {
    ariaLabel: 'Voice session status'
  },
  connection: {
    connected: {
      text: 'Connected',
      tooltip: 'Voice connection active - you can speak and hear others'
    },
    connecting: {
      text: 'Connecting',
      tooltip: 'Establishing voice connection...'
    },
    reconnecting: {
      text: 'Reconnecting',
      tooltip: 'Connection lost - attempting to reconnect...'
    },
    error: {
      text: 'Error',
      tooltip: 'Connection error - check your internet and try again'
    },
    disconnected: {
      text: 'Disconnected',
      tooltip: 'Not connected to voice session'
    }
  },
  mode: {
    ariaLabelPrefix: 'Voice mode: '
  },
  mute: {
    indicator: 'Microphone is muted',
    label: 'Muted'
  },
  session: {
    label: 'Session:'
  },
  local: {
    speaking: 'You are speaking',
    muted: 'You are muted',
    notSpeaking: 'You are not speaking',
    label: 'You',
    voiceLevel: 'Voice level: {level}%'
  },
  participants: {
    header: 'Speaking ({count})',
    ariaLabel: '{count} participant{plural} speaking',
    voiceLevel: '{name} voice level: {level}%'
  }
} as const;

/**
 * Voice Permission Prompt Messages
 */
export const voicePermission = {
  errors: {
    permission_denied: {
      title: 'Microphone Access Denied',
      message: 'Mythbound needs microphone access for voice features.',
      guidance: [
        'Click the lock/settings icon in your browser address bar',
        'Find "Microphone" in the site permissions',
        'Change the setting to "Allow"',
        'Refresh the page and try again'
      ]
    },
    no_microphone: {
      title: 'No Microphone Found',
      message: 'We couldn\'t detect a microphone on your device.',
      guidance: [
        'Connect a microphone or headset',
        'Check your system sound settings',
        'Make sure your microphone is not disabled',
        'Try refreshing the page after connecting'
      ]
    },
    browser_unsupported: {
      title: 'Browser Not Supported',
      message: 'Your browser doesn\'t support the audio features needed for voice chat.',
      guidance: [
        'Use a modern browser like Chrome, Firefox, or Edge',
        'Make sure your browser is up to date',
        'Enable WebRTC in your browser settings'
      ]
    },
    connection_failed: {
      title: 'Connection Failed',
      message: 'Couldn\'t connect to the voice server.',
      guidance: [
        'Check your internet connection',
        'Try refreshing the page',
        'Wait a moment and try again'
      ]
    },
    connection_lost: {
      title: 'Connection Lost',
      message: 'The voice connection was interrupted.',
      guidance: [
        'Check your internet connection',
        'We\'ll try to reconnect automatically',
        'Click Retry if the issue persists'
      ]
    },
    generic: {
      title: 'Voice Error',
      message: 'Something went wrong with voice features.',
      guidance: [
        'Try refreshing the page',
        'Check your browser permissions',
        'Contact support if the issue persists'
      ]
    }
  },
  prompt: {
    title: 'Enable Microphone',
    message: 'Mythbound needs microphone access for voice features.',
    guidance: [
      'Click "Allow" when your browser asks for permission',
      'You can change this setting anytime in your browser'
    ]
  },
  states: {
    checking: {
      title: 'Checking Permissions',
      message: 'Please allow microphone access when prompted...'
    }
  },
  buttons: {
    enable: {
      checking: 'Checking...',
      label: 'Enable Microphone',
      ariaLabel: 'Enable microphone',
      ariaLabelChecking: 'Checking microphone permission'
    },
    retry: {
      label: 'Retry',
      ariaLabel: 'Retry microphone access'
    },
    fallback: {
      label: 'Use Text Only',
      ariaLabel: 'Use text-only mode instead of voice'
    },
    dismiss: {
      label: 'Close',
      ariaLabel: 'Close permission prompt'
    }
  },
  capabilities: {
    title: 'Browser Capabilities:',
    webRTC: 'WebRTC:',
    audio: 'Audio:',
    webSocket: 'WebSocket:',
    supported: 'supported',
    notSupported: 'not supported'
  }
} as const;

/**
 * Voice Diagnostics Messages
 */
export const voiceDiagnostics = {
  header: {
    title: 'Voice Diagnostics',
    ariaLabel: 'Voice diagnostics panel'
  },
  sections: {
    connection: 'Connection',
    capture: 'Capture (Mic → Server)',
    playback: 'Playback (Server → Speaker)',
    mode: 'Voice Mode',
    buffer: 'Buffer Preset',
    quality: 'Quality',
    session: 'Session'
  },
  labels: {
    state: 'State',
    webSocket: 'WebSocket',
    duration: 'Duration',
    rtt: 'RTT',
    active: 'Active',
    chunksSent: 'Chunks Sent',
    buffer: 'Buffer',
    sampleRate: 'Sample Rate',
    preset: 'Preset',
    received: 'Received',
    played: 'Played',
    dropped: 'Dropped',
    latency: 'Latency',
    jitter: 'Jitter',
    packetLoss: 'Packet Loss',
    mode: 'Mode',
    pathway: 'Pathway',
    forcePathway: 'Force Pathway:',
    underruns: 'Underruns',
    overruns: 'Overruns',
    session: 'Session:',
    participant: 'Participant:',
    lastUpdate: 'Last update'
  },
  common: {
    yes: 'Yes',
    no: 'No',
    notAvailable: 'N/A'
  },
  tooltips: {
    rtt: 'Round-Trip Time: Total time for a message to travel to server and back. Lower is better.',
    latency: 'Audio latency: Delay from when audio is sent to when it\'s played. Lower is better for real-time conversation.',
    jitter: 'Jitter: Variation in packet arrival times. Higher jitter can cause audio stuttering. Lower is better.',
    packetLoss: 'Packet Loss: Percentage of audio packets lost in transit. Higher loss causes audio dropouts. Lower is better.',
    presetLocked: 'Preset locked - adaptive buffering disabled',
    setBufferPreset: 'Set buffer preset to {label}: {description}'
  },
  pathway: {
    auto: 'Auto',
    sts: 'STS',
    sttLlmTts: 'STT→LLM→TTS',
    ariaLabelAuto: 'Set voice pathway to automatic',
    ariaLabelSts: 'Set voice pathway to Speech-to-Speech',
    ariaLabelSttLlmTts: 'Set voice pathway to Speech-to-Text to LLM to Text-to-Speech'
  }
} as const;

/**
 * Voice Bridge Info Messages
 */
export const voiceBridge = {
  help: {
    title: 'Voice Bridge & Discord',
    sections: {
      whoCanHear: {
        heading: 'Who Can Hear You?',
        content: `**Web users hear:**
        • Other web users in the same session ✓
        • Discord users via the Myth Master bot relay (when bridge is active) ✓
        • The Myth Master (MM) bot responses ✓

        **Discord users hear:**
        • Other Discord users in the same voice channel ✓
        • Web users via the MM bot proxy (when bridge is active) ✓
        • The Myth Master bot responses ✓

        **Note:** When the bridge is not yet active, web and Discord users cannot hear each other directly, but the Myth Master hears everyone.`
      },
      discord: {
        heading: 'Discord Integration',
        content: `The Myth Master (MM) bot sits in both your web session and the Discord voice channel. It acts as a bridge:

        • Web → Discord: Your web audio is sent through the MM bot into the Discord channel
        • Discord → Web: Discord audio is picked up by the MM bot and relayed to your browser

        This means you can play with friends regardless of whether they use the web app or Discord.`
      },
      modes: {
        heading: 'Voice Modes',
        content: `All voice modes work the same for both Discord and web users:
        • **Transcribe All**: The MM™ processes everything you say
        • **Hey MM™**: The MM™ only responds when you say "Hey MM"
        • **Voice Off**: Voice transcription disabled
        
        Your mode selection is synchronized across platforms for your campaign.`
      },
      status: {
        heading: 'Current Status',
        content: `**Active Now:**
        • Web ↔ Web: ✓ Web users can hear each other
        • Web → MM bot: ✓ The Myth Master hears you
        • Discord → MM bot: ✓ The Myth Master hears Discord users

        **When Bridge is Enabled:**
        • Web ↔ Discord: ✓ Cross-platform voice via MM bot relay`
      }
    }
  },
  status: {
    connected: {
      text: 'Bridge Active',
      description: 'Discord users can hear you'
    },
    connecting: {
      text: 'Connecting...',
      description: 'Establishing Discord connection'
    },
    relayPending: {
      text: 'Web Only',
      description: 'Discord relay not yet available'
    },
    disconnected: {
      text: 'Disconnected',
      description: 'Voice bridge not connected'
    }
  },
  buttons: {
    learnMore: {
      label: 'Learn more',
      ariaLabel: 'Learn more about voice bridge and Discord integration',
      title: 'Learn more about voice bridge'
    },
    help: {
      ariaLabel: 'Open voice bridge information',
      title: 'Voice bridge info'
    },
    close: {
      ariaLabel: 'Close voice bridge help modal'
    },
    acknowledge: {
      label: 'Got it'
    }
  },
  inline: {
    helpButton: {
      title: 'Learn more about voice bridge'
    },
    relayPending: 'Discord relay pending'
  },
  modal: {
    currentStatus: 'Your Current Status',
    pendingNote: '⏳ Full Discord relay is a planned feature'
  },
  audience: {
    label: 'Audience:',
    none: 'No listeners',
    relayComingSoon: 'Discord relay is coming soon. For now, only web users can hear you.',
    // Added 2026-02-23: audience breakdown labels for localization readiness
    webUser: '{count} web user',
    webUsers: '{count} web users',
    discordUser: '{count} Discord user',
    discordUsers: '{count} Discord users',
    webPlatform: 'Web',
    discordPlatform: 'Discord'
  },
  onboarding: {
    summary: 'The MM bot bridges web and Discord voice so everyone can play together.',
    webToDiscord: 'Web users are heard by Discord users via the MM bot.',
    discordToWeb: 'Discord users are heard by web users via the MM bot.',
    relayNote: 'Discord relay coming soon'
  },
  floating: {
    title: 'Discord Voice Relay',
    minimize: {
      ariaLabel: 'Minimize voice bridge widget',
      title: 'Minimize'
    },
    pin: {
      ariaLabelPin: 'Pin to sidebar',
      ariaLabelUnpin: 'Unpin from sidebar'
    },
    popout: {
      ariaLabel: 'Open in new window (coming soon)',
      title: 'Popout (coming soon)'
    },
    defaultTrigger: {
      title: 'Voice bridge status'
    },
    modalTriggerFallback: 'Voice Bridge Info'
  }
} as const;

/**
 * Voice Panel Messages
 * Updated 2026-02-23: Section 5 Voice Bridge UI Wiring - VoicePanel overlay strings
 */
export const voicePanel = {
  region: {
    ariaLabel: 'Voice session panel'
  },
  title: 'Voice',
  minimized: {
    ariaLabel: 'Voice panel (minimized). {count} participant(s). Click to expand.',
    title: 'Voice Panel',
    expand: 'Expand voice panel'
  },
  expanded: {
    minimize: {
      ariaLabel: 'Minimize voice panel',
      title: 'Minimize'
    },
    close: {
      ariaLabel: 'Close voice panel',
      title: 'Close'
    }
  },
  participantBadge: '{count}',
  connectionDot: {
    connected: 'Connected',
    connecting: 'Connecting',
    error: 'Connection error',
    disconnected: 'Disconnected'
  }
} as const;

/**
 * Voice Toast Messages
 * Used by voiceToastService for toast notifications
 */
export const voiceToasts = {
  error: {
    title: 'Voice Error'
  },
  modeChanged: {
    title: 'Voice Mode Changed',
    description: 'Mode changed to:'
  },
  joined: {
    title: 'Voice Session Joined',
    description: 'You are now in the voice session.'
  },
  left: {
    title: 'Left Voice Session',
    description: 'You have left the voice session.'
  },
  reconnecting: {
    title: 'Reconnecting...',
    description: 'Attempting to reconnect to voice session.'
  },
  reconnected: {
    title: 'Reconnected',
    description: 'Voice connection restored.'
  },
  relay: {
    enabled: {
      title: 'Discord Relay Enabled',
      description: 'Discord users can now hear you.'
    },
    disabled: {
      title: 'Discord Relay Disabled',
      description: 'Discord relay has been turned off.'
    },
    failed: {
      title: 'Relay Connection Failed',
      description: 'Could not connect to Discord voice channel.'
    }
  }
} as const;

/**
 * Voice Session Error Messages
 * Maps error codes to user-friendly messages
 * Used by voiceSessionService.getErrorMessage()
 */
export const voiceSessionErrors = {
  // 2xxx: Voice Session Errors
  2001: 'Voice session not found.',
  2002: 'Session full (6/6 participants). Try again later.',
  2003: 'This voice session has ended.',
  2004: 'Not authorized for this campaign.',
  2005: 'You are already in this voice session.',
  2006: 'Invalid voice mode selected.',
  2007: 'Failed to create voice session.',
  2008: 'Reconnection window expired. Please join again.',
  
  // 3xxx: Audio Pipeline Errors
  3001: 'Microphone access denied. Check browser permissions.',
  3002: 'No microphone detected. Please connect a microphone.',
  3003: 'Browser does not support voice features. Try Chrome, Firefox, or Edge.',
  3004: 'Audio initialization failed. Try refreshing the page.',
  3005: 'Audio playback error. Check your speakers/headphones.',
  3006: 'Audio buffer underrun. Voice may be choppy - try switching to Smooth buffer preset.',
  3007: 'Audio buffer overrun. Voice may drop out - try switching to Responsive buffer preset.',
  3008: 'Excessive packet loss detected. Check your network connection.',
  
  // 4xxx: Discord Relay Errors
  4001: 'Discord bot is not connected. Contact your GM to restart the bot.',
  4002: 'Discord voice channel not found. Make sure the channel still exists.',
  4003: 'Failed to connect Discord relay. The bot may not have voice permissions.',
  4004: 'Discord relay disconnected unexpectedly. Attempting to reconnect...',
  4005: 'Bot is not in your Discord server. Use the invite link to add it.',
  
  // 9xxx: Generic Errors
  9001: 'Connection error. Please check your network.',
  9999: 'Something went wrong. Please try again.'
} as const;

/**
 * Helper function to format messages with placeholders
 * (Simple implementation - can be enhanced with i18n library later)
 */
export function formatMessage(template: string, values: Record<string, string | number>): string {
  let result = template;
  for (const [key, value] of Object.entries(values)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
  }
  return result;
}

